import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminUsers } from '../../api/adminClient'
import { Building2, Shield } from 'lucide-react'

type Tab = 'platform' | 'company'

export default function AdminUsers() {
  const { t } = useTranslation('portal')
  const [tab, setTab] = useState<Tab>('platform')
  const queryClient = useQueryClient()

  const { data: platformData, isLoading: platformLoading } = useQuery({
    queryKey: ['admin', 'users', 'platform'],
    queryFn: async () => (await adminUsers.platform()).data,
    enabled: tab === 'platform',
  })
  const { data: companyData, isLoading: companyLoading } = useQuery({
    queryKey: ['admin', 'users', 'company'],
    queryFn: async () => (await adminUsers.company()).data,
    enabled: tab === 'company',
  })

  const suspendMutation = useMutation({
    mutationFn: (id: string) => adminUsers.suspend(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
  const activateMutation = useMutation({
    mutationFn: (id: string) => adminUsers.activate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
  const resetPwMutation = useMutation({
    mutationFn: (id: string) => adminUsers.forcePasswordReset(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })

  const isLoading = tab === 'platform' ? platformLoading : companyLoading
  const items = tab === 'platform' ? platformData?.items ?? [] : companyData?.items ?? []
  const total = tab === 'platform' ? platformData?.total ?? 0 : companyData?.total ?? 0

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('admin.users.title')}</h1>

      <div className="flex gap-2 mb-6">
        <button
          type="button"
          onClick={() => setTab('platform')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'platform'
              ? 'bg-emerald-600 text-white'
              : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Shield className="w-4 h-4" />
          {t('admin.users.platformUsers')}
        </button>
        <button
          type="button"
          onClick={() => setTab('company')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            tab === 'company'
              ? 'bg-emerald-600 text-white'
              : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Building2 className="w-4 h-4" />
          {t('admin.users.companyUsers')}
        </button>
      </div>

      {isLoading ? (
        <div className="text-gray-500">{t('admin.common.loading')}</div>
      ) : (
        <div className="rounded-xl bg-white border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-gray-500 font-medium text-sm">{t('admin.users.email')}</th>
                <th className="px-4 py-3 text-gray-500 font-medium text-sm">{t('admin.users.role')}</th>
                {tab === 'company' && (
                  <th className="px-4 py-3 text-gray-500 font-medium text-sm">{t('admin.users.company')}</th>
                )}
                <th className="px-4 py-3 text-gray-500 font-medium text-sm">{t('admin.common.status')}</th>
                <th className="px-4 py-3 text-gray-500 font-medium text-sm">{t('admin.common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u: { id: string; email: string; role: string; is_active: string; company_name?: string }) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-gray-900">{u.email}</td>
                  <td className="px-4 py-3 text-gray-600">{u.role}</td>
                  {tab === 'company' && (
                    <td className="px-4 py-3 text-gray-600">{u.company_name ?? '—'}</td>
                  )}
                  <td className="px-4 py-3">
                    <span
                      className={
                        u.is_active === 'true'
                          ? 'text-emerald-600'
                          : 'text-amber-600'
                      }
                    >
                      {u.is_active === 'true' ? t('admin.users.active') : t('admin.users.suspended')}
                    </span>
                  </td>
                  <td className="px-4 py-3 flex gap-2">
                    {u.is_active === 'true' ? (
                      <button
                        type="button"
                        onClick={() => window.confirm(t('admin.users.suspendConfirm')) && suspendMutation.mutate(u.id)}
                        className="text-sm text-amber-600 hover:underline"
                      >
                        {t('admin.users.suspend')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => activateMutation.mutate(u.id)}
                        className="text-sm text-emerald-600 hover:underline"
                      >
                        {t('admin.users.activate')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        window.confirm(t('admin.users.forceResetConfirm')) &&
                        resetPwMutation.mutate(u.id)
                      }
                      className="text-sm text-gray-600 hover:underline"
                    >
                      {t('admin.users.forceResetPw')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="px-4 py-2 text-gray-500 text-sm border-t border-gray-200 bg-gray-50">
            {t('admin.common.total')}: {total}
          </div>
        </div>
      )}
    </div>
  )
}
